import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLanguage } from "../i18n";
import "./MyProfile.css";

const ACCEPTED_TYPES = new Set(["image/jpeg", "image/png", "image/gif", "image/webp", "image/avif"]);
const MAX_IMAGE_BYTES = 20 * 1024 * 1024;

function when(value) {
  return new Date(value).toLocaleString();
}

function initials(user) {
  return (user?.name || user?.username || "B").trim().charAt(0).toUpperCase();
}

export default function MyProfile() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [posts, setPosts] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [caption, setCaption] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const fileInputRef = useRef(null);
  const profileType = searchParams.get("type") === "creator" ? t("creatorProfile") : t("myProfile");
  const albumMode = searchParams.get("tab") === "album";

  useEffect(() => {
    if (!file) {
      setPreviewUrl("");
      return undefined;
    }
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  const loadFeed = async ({ append = false, before = null } = {}) => {
    const params = new URLSearchParams();
    if (before) params.set("before", before);
    if (albumMode) params.set("mine", "true");
    const query = params.size ? `?${params.toString()}` : "";
    const response = await fetch(`/api/posts/feed${query}`, { credentials: "include" });
    const data = await response.json();
    if (!response.ok || !data.ok) throw new Error(data.error || "Could not load the photo feed.");
    setPosts((current) => append ? [...current, ...data.posts] : data.posts);
    setCursor(data.nextCursor || null);
  };

  useEffect(() => {
    let active = true;
    Promise.all([
      fetch("/api/me", { credentials: "include" }).then(async (response) => {
        const data = await response.json();
        if (!response.ok || !data.ok) throw new Error("Sign in to open your profile.");
        return data.user;
      }),
      loadFeed()
    ]).then(([profile]) => {
      if (active) setUser(profile);
    }).catch((loadError) => {
      if (!active) return;
      if (loadError.message === t("signInProfile")) navigate("/register", { replace: true, state: { from: "/my-profile" } });
      else setError(loadError.message === "Failed to fetch" ? t("profileOffline") : loadError.message);
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
    // Load the initial session and feed once when the page opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, albumMode]);

  const publish = async (event) => {
    event.preventDefault();
    setError("");
    setNotice("");
    if (!file) return setError(t("chooseImageFirst"));
    if (!ACCEPTED_TYPES.has(file.type)) return setError(t("supportedImageOnly"));
    if (file.size > MAX_IMAGE_BYTES) return setError(t("imageTooLarge"));

    setPublishing(true);
    try {
      const uploadResponse = await fetch("/api/media/upload/url", {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contentType: file.type })
      });
      const upload = await uploadResponse.json();
      if (!uploadResponse.ok || !upload.ok) throw new Error(upload.error || t("uploadPreparingError"));

      const stored = await fetch(upload.uploadUrl, { method: "PUT", headers: { "Content-Type": file.type }, body: file });
      if (!stored.ok) throw new Error(t("uploadFailed"));

      const confirmResponse = await fetch("/api/media/upload/confirm", {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ key: upload.key, contentType: file.type })
      });
      const confirm = await confirmResponse.json();
      if (!confirmResponse.ok || !confirm.ok) throw new Error(confirm.error || t("uploadConfirmFailed"));

      const postResponse = await fetch("/api/posts", {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mediaKey: upload.key, mediaType: file.type, caption })
      });
      const post = await postResponse.json();
      if (!postResponse.ok || !post.ok) throw new Error(post.error || t("publishFailed"));
      setPosts((current) => [post.post, ...current]);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setCaption("");
      setNotice(t("publishedNotice"));
    } catch (publishError) {
      setError(publishError.message === "Failed to fetch" ? t("uploadUnavailable") : publishError.message);
    } finally {
      setPublishing(false);
    }
  };

  const reactToPost = async (post, reaction) => {
    const nextReaction = post.myReaction === reaction ? null : reaction;
    try {
      const response = await fetch(`/api/posts/${post.id}/reaction`, {
        method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reaction: nextReaction })
      });
      const data = await response.json();
      if (!response.ok || !data.ok) throw new Error(data.error || t("reactionFailed"));
      setPosts((current) => current.map((item) => item.id === post.id
        ? { ...item, myReaction: data.reaction, likes: data.likes, dislikes: data.dislikes }
        : item));
    } catch (reactionError) {
      setError(reactionError.message);
    }
  };

  const sharePost = async (post) => {
    setError("");
    try {
      const response = await fetch(`/api/posts/${post.id}/share`, { method: "POST", credentials: "include" });
      const data = await response.json();
      if (!response.ok || !data.ok) throw new Error(data.error || t("shareFailed"));
      const url = `${window.location.origin}${data.path}`;
      if (navigator.share) await navigator.share({ title: "BublyUp photo", url });
      else {
        await navigator.clipboard.writeText(url);
        setNotice(t("copiedNotice"));
      }
      setPosts((current) => current.map((item) => item.id === post.id ? { ...item, shares: data.shares } : item));
    } catch (shareError) {
      if (shareError.name !== "AbortError") setError(shareError.message || "Could not share this post.");
    }
  };

  if (loading) return <main className="my-profile-page"><p className="my-profile-loading">{t("loadingProfile")}</p></main>;

  return (
    <main className="my-profile-page">
      <div className="my-profile-layout">
        <section className="my-profile-hero">
          {user?.avatar && /^https?:\/\//i.test(user.avatar)
            ? <img className="my-profile-avatar" src={user.avatar} alt="Your profile" referrerPolicy="no-referrer" />
            : <div className="my-profile-avatar my-profile-avatar-initial">{initials(user)}</div>}
          <div className="my-profile-identity">
            <span>BUBLYUP · {profileType.toUpperCase()}</span>
            <h1>{user?.name || user?.username || "Welcome to BublyUp"}</h1>
            <p>{user?.username ? `@${user.username}` : t("firstMoment")}</p>
          </div>
          <div className="my-profile-count"><strong>{posts.length}</strong><span>{t("recentPhotosCount")}</span></div>
        </section>

        {error && <div className="my-profile-alert" role="alert">{error}</div>}
        {notice && <div className="my-profile-notice" role="status">{notice}</div>}

        <form className="my-profile-composer" onSubmit={publish}>
          <div><span className="my-profile-kicker">{t("shareMoment")}</span><h2>{t("publishPhoto")}</h2><p>{t("photoIntro")}</p></div>
          <label className="my-profile-file-label">
            <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp,image/avif" onChange={(event) => setFile(event.target.files?.[0] || null)} />
            <span>{file ? file.name : t("choosePhoto")}</span>
            <small>{t("photoFormats")}</small>
          </label>
          {previewUrl && <img className="my-profile-preview" src={previewUrl} alt="Selected upload preview" />}
          <label className="my-profile-caption-label">{t("caption")}<textarea value={caption} onChange={(event) => setCaption(event.target.value)} maxLength={2000} placeholder={t("captionPlaceholder")} /></label>
          <div className="my-profile-composer-footer"><span>{caption.length}/2000</span><button type="submit" disabled={publishing || !file}>{publishing ? t("publishing") : t("publish")}</button></div>
        </form>

        <section className="my-profile-feed" aria-label="Recent photos">
          <div className="my-profile-feed-heading"><div><span className="my-profile-kicker">{albumMode ? t("myProfile") : t("community")}</span><h2>{albumMode ? t("myAlbum") : t("recentPhotos")}</h2></div></div>
          {posts.length === 0 ? <div className="my-profile-empty"><div>✦</div><h3>{t("profileReady")}</h3><p>{t("firstMoment")}</p></div> : posts.map((post) => (
            <article className="my-profile-post" key={post.id}>
              <header className="my-profile-post-header">
                {post.avatar && /^https?:\/\//i.test(post.avatar) ? <img src={post.avatar} alt="" referrerPolicy="no-referrer" /> : <span className="my-profile-mini-avatar">{initials(post)}</span>}
                <div><strong>{post.name || post.username || "BublyUp member"}</strong><small>{post.username ? `@${post.username} · ` : ""}{when(post.createdAt)}</small></div>
                {post.isMine && <span className="my-profile-own-badge">{t("yourPost")}</span>}
              </header>
              {post.caption && <p className="my-profile-post-caption">{post.caption}</p>}
              <img className="my-profile-post-image" src={post.imageUrl} alt={post.caption || t("photoAlt")} loading="lazy" />
              <div className="my-profile-reaction-counts"><span>{post.likes} likes</span><span>{post.dislikes} dislikes</span><span>{post.shares} shares</span></div>
              <div className="my-profile-post-actions">
                <button className={post.myReaction === "like" ? "selected" : ""} type="button" onClick={() => reactToPost(post, "like")}>{t("like")}</button>
                <button className={post.myReaction === "dislike" ? "selected" : ""} type="button" onClick={() => reactToPost(post, "dislike")}>{t("dislike")}</button>
                <button type="button" onClick={() => sharePost(post)}>{t("share")}</button>
              </div>
            </article>
          ))}
          {cursor && <button className="my-profile-load-more" type="button" onClick={() => loadFeed({ append: true, before: cursor }).catch((loadError) => setError(loadError.message))}>{t("loadMore")}</button>}
        </section>
      </div>
    </main>
  );
}
